import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { cn } from "@/lib/cn";
import { FILTER_ACTIVE_CLASS, useFilterHighlight } from "@/components/molecules/FilterBar";

interface Props {
  id: string;
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Lower bound as a `datetime-local` string (`YYYY-MM-DDTHH:mm`). */
  min?: string;
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Parse a `datetime-local` string into a local Date, or null when empty/invalid. */
function parseLocal(v: string | undefined | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v ?? "");
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Serialise a Date back to the `datetime-local` contract (`YYYY-MM-DDTHH:mm`). */
function toLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** 24h → { hour12 (1-12), meridiem }. */
function to12h(h24: number): { h12: number; meridiem: "AM" | "PM" } {
  const meridiem = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { h12, meridiem };
}

/** { hour12, meridiem } → 24h. */
function to24h(h12: number, meridiem: "AM" | "PM"): number {
  if (meridiem === "AM") return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0..59
const GAP = 4; // px gap between the trigger and the popup

/**
 * Cross-browser date-time picker with a consistent calendar + hour/minute/AM-PM
 * UI. Replaces the native `<input type="datetime-local">`, whose popup Safari
 * renders without a time column. Public API (value/onChange as a
 * `datetime-local` string) is unchanged so existing call sites keep working.
 *
 * The popup is portalled to <body> and positioned with `fixed` coordinates so
 * it (a) escapes clipping by scrollable drawers and (b) flips above the trigger
 * when there isn't room below — matching the native picker's viewport awareness.
 */
export function DiaryDateTimeField({ id, label, value, onChange, disabled, min }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const selected = useMemo(() => parseLocal(value), [value]);
  const minDate = useMemo(() => parseLocal(min), [min]);

  // The month currently shown in the calendar grid.
  const [viewDate, setViewDate] = useState(() => selected ?? minDate ?? new Date());

  // Re-centre the grid on the selected month whenever the popup opens.
  useEffect(() => {
    if (open) setViewDate(selected ?? minDate ?? new Date());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure the trigger + popup and place the popup within the viewport,
  // flipping above when there isn't enough room below.
  const reposition = () => {
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popupH = popup?.offsetHeight ?? 340;
    const popupW = popup?.offsetWidth ?? 420;
    const vpH = window.innerHeight;
    const vpW = window.innerWidth;

    const spaceBelow = vpH - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < popupH + GAP && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - popupH - GAP : rect.bottom + GAP;
    top = Math.max(8, Math.min(top, vpH - popupH - 8));
    const left = Math.max(8, Math.min(rect.left, vpW - popupW - 8));
    setCoords({ top, left });
  };

  // Position on open, and keep it anchored while scrolling/resizing.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    const onScroll = () => reposition();
    const onResize = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const displayValue = useMemo(() => {
    if (!selected) return "";
    const { h12, meridiem } = to12h(selected.getHours());
    return `${pad2(selected.getDate())}/${pad2(selected.getMonth() + 1)}/${selected.getFullYear()}, ${pad2(h12)}:${pad2(selected.getMinutes())} ${meridiem}`;
  }, [selected]);

  // Working date used to fill in the "other half" when only date or only time
  // is picked first. Falls back to min, then now.
  const base = selected ?? minDate ?? new Date();
  const { h12: curH12, meridiem: curMeridiem } = to12h(base.getHours());
  const curMinute = base.getMinutes();

  /** Clamp below-min values up to the minimum, then emit in the string contract. */
  const emit = (d: Date) => {
    const next = minDate && d.getTime() < minDate.getTime() ? new Date(minDate) : d;
    onChange(toLocal(next));
  };

  const pickDay = (day: Date) => {
    emit(new Date(day.getFullYear(), day.getMonth(), day.getDate(), base.getHours(), base.getMinutes()));
  };
  const pickHour = (h12: number) => {
    const d = new Date(base);
    d.setHours(to24h(h12, curMeridiem));
    emit(d);
  };
  const pickMinute = (mi: number) => {
    const d = new Date(base);
    d.setMinutes(mi);
    emit(d);
  };
  const pickMeridiem = (meridiem: "AM" | "PM") => {
    const d = new Date(base);
    d.setHours(to24h(curH12, meridiem));
    emit(d);
  };

  // Build a 6-week (42-cell) grid starting on the Sunday on/before the 1st.
  const gridDays = useMemo(() => {
    const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const gridStart = new Date(first);
    gridStart.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [viewDate]);

  const today = startOfDay(new Date());
  const minDay = minDate ? startOfDay(minDate) : null;

  return (
    <div className="w-full min-w-0">
      <label htmlFor={id} className="text-xs font-medium text-bridge-muted block mb-1">
        {label}
      </label>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "h-10 w-full px-3 flex items-center justify-between gap-2 rounded-md border text-sm outline-none transition-colors",
          "border-bridge-border/70 bg-bridge-surface/70 text-bridge-fg",
          open ? "border-bridge-accent/60 shadow-ring" : "hover:border-bridge-accent/40",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <span className={cn("truncate", !displayValue && "text-bridge-muted")}>
          {displayValue || "Select date & time"}
        </span>
        <Calendar className="w-4 h-4 shrink-0 text-bridge-muted" />
      </button>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            data-floating-surface
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              visibility: coords ? "visible" : "hidden",
            }}
            className="z-[9999] flex rounded-md border border-bridge-border bg-bridge-surface shadow-xl overflow-hidden"
          >
            {/* ── Calendar ─────────────────────────────────────────── */}
            <div className="p-3 w-[248px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-bridge-fg">
                  {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-1 rounded text-bridge-muted hover:bg-bridge-bg hover:text-bridge-fg"
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded text-bridge-muted hover:bg-bridge-bg hover:text-bridge-fg"
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 mb-1">
                {WEEKDAYS.map((w, i) => (
                  <div key={i} className="h-7 flex items-center justify-center text-[11px] font-semibold text-bridge-muted">
                    {w}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {gridDays.map((day) => {
                  const inMonth = day.getMonth() === viewDate.getMonth();
                  const isSelected = selected != null && sameDay(day, selected);
                  const isToday = sameDay(day, today);
                  const isDisabled = minDay != null && startOfDay(day).getTime() < minDay.getTime();
                  return (
                    <button
                      key={day.getTime()}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => pickDay(day)}
                      className={cn(
                        "h-8 w-8 mx-auto flex items-center justify-center text-sm transition-colors",
                        isSelected
                          ? "bg-bridge-accent text-white font-semibold"
                          : inMonth
                            ? "text-bridge-fg hover:bg-bridge-bg"
                            : "text-bridge-muted/50 hover:bg-bridge-bg",
                        !isSelected && isToday && "border border-bridge-fg/40",
                        isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent",
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-bridge-border/60">
                <button
                  type="button"
                  className="text-xs font-medium text-bridge-accent hover:underline"
                  onClick={() => onChange("")}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-xs font-medium text-bridge-accent hover:underline"
                  onClick={() => {
                    const now = new Date();
                    now.setSeconds(0, 0);
                    setViewDate(now);
                    emit(now);
                  }}
                >
                  Now
                </button>
              </div>
            </div>

            {/* ── Time columns ─────────────────────────────────────── */}
            <div className="flex border-l border-bridge-border/60">
              <TimeColumn items={HOURS_12} render={(h) => pad2(h)} selected={curH12} onPick={pickHour} loop />
              <TimeColumn items={MINUTES} render={(m) => pad2(m)} selected={curMinute} onPick={pickMinute} loop />
              <TimeColumn items={["AM", "PM"] as const} render={(m) => m} selected={curMeridiem} onPick={pickMeridiem} />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Parse a `YYYY-MM-DD` string into a local Date, or null when empty/invalid. */
function parseDay(v: string | undefined | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v ?? "");
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Serialise a Date back to the `YYYY-MM-DD` date-only contract. */
function toDay(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

interface DateProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Lower bound as a `YYYY-MM-DD` string. */
  min?: string;
  /** Upper bound as a `YYYY-MM-DD` string. */
  max?: string;
}

/**
 * Date-only sibling of {@link DiaryDateTimeField}. Renders the identical styled
 * calendar popup, minus the time columns, against a `YYYY-MM-DD` value contract.
 * Replaces the native `<input type="date">`, whose display format follows the
 * OS/browser locale (MM/DD/YYYY on a US-region Safari) and whose picker button
 * Safari omits — so date fields look and behave the same across browsers.
 */
export function DiaryDateField({ id, value, onChange, disabled, placeholder = "Select date", min, max }: DateProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const selected = useMemo(() => parseDay(value), [value]);
  const minDate = useMemo(() => parseDay(min), [min]);
  const maxDate = useMemo(() => parseDay(max), [max]);

  const [viewDate, setViewDate] = useState(() => selected ?? minDate ?? new Date());

  useEffect(() => {
    if (open) setViewDate(selected ?? minDate ?? new Date());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const reposition = () => {
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popupH = popup?.offsetHeight ?? 320;
    const popupW = popup?.offsetWidth ?? 248;
    const vpH = window.innerHeight;
    const vpW = window.innerWidth;

    const spaceBelow = vpH - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < popupH + GAP && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - popupH - GAP : rect.bottom + GAP;
    top = Math.max(8, Math.min(top, vpH - popupH - 8));
    const left = Math.max(8, Math.min(rect.left, vpW - popupW - 8));
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    const onScroll = () => reposition();
    const onResize = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const displayValue = useMemo(() => {
    if (!selected) return "";
    return `${pad2(selected.getDate())}/${pad2(selected.getMonth() + 1)}/${selected.getFullYear()}`;
  }, [selected]);

  const pickDay = (day: Date) => {
    let next = day;
    if (minDate && startOfDay(next).getTime() < startOfDay(minDate).getTime()) next = minDate;
    if (maxDate && startOfDay(next).getTime() > startOfDay(maxDate).getTime()) next = maxDate;
    onChange(toDay(startOfDay(next)));
    setOpen(false);
  };

  const gridDays = useMemo(() => {
    const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const gridStart = new Date(first);
    gridStart.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [viewDate]);

  const today = startOfDay(new Date());
  const minDay = minDate ? startOfDay(minDate) : null;
  const maxDay = maxDate ? startOfDay(maxDate) : null;
  const filterActive = useFilterHighlight(Boolean(value));

  return (
    <div className="w-full min-w-0">
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "h-10 w-full px-3 flex items-center justify-between gap-2 rounded-md border text-sm outline-none transition-colors",
          "border-bridge-border/70 bg-bridge-surface/70 text-bridge-fg",
          filterActive && FILTER_ACTIVE_CLASS,
          open ? "border-bridge-accent/60 shadow-ring" : "hover:border-bridge-accent/40",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <span className={cn("truncate", !displayValue && "text-bridge-muted")}>
          {displayValue || placeholder}
        </span>
        <Calendar
          className={cn("w-4 h-4 shrink-0", filterActive ? "text-bridge-accent" : "text-bridge-muted")}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            data-floating-surface
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              visibility: coords ? "visible" : "hidden",
            }}
            className="z-[9999] rounded-md border border-bridge-border bg-bridge-surface shadow-xl overflow-hidden"
          >
            <div className="p-3 w-[248px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-bridge-fg">
                  {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-1 rounded text-bridge-muted hover:bg-bridge-bg hover:text-bridge-fg"
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded text-bridge-muted hover:bg-bridge-bg hover:text-bridge-fg"
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 mb-1">
                {WEEKDAYS.map((w, i) => (
                  <div key={i} className="h-7 flex items-center justify-center text-[11px] font-semibold text-bridge-muted">
                    {w}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {gridDays.map((day) => {
                  const inMonth = day.getMonth() === viewDate.getMonth();
                  const isSelected = selected != null && sameDay(day, selected);
                  const isToday = sameDay(day, today);
                  const isDisabled =
                    (minDay != null && startOfDay(day).getTime() < minDay.getTime()) ||
                    (maxDay != null && startOfDay(day).getTime() > maxDay.getTime());
                  return (
                    <button
                      key={day.getTime()}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => pickDay(day)}
                      className={cn(
                        "h-8 w-8 mx-auto flex items-center justify-center text-sm transition-colors",
                        isSelected
                          ? "bg-bridge-accent text-white font-semibold"
                          : inMonth
                            ? "text-bridge-fg hover:bg-bridge-bg"
                            : "text-bridge-muted/50 hover:bg-bridge-bg",
                        !isSelected && isToday && "border border-bridge-fg/40",
                        isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent",
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>

              {value && (
                <div className="flex items-center justify-end mt-2 pt-2 border-t border-bridge-border/60">
                  <button
                    type="button"
                    className="text-xs font-medium text-bridge-accent hover:underline"
                    onClick={() => {
                      onChange("");
                      setOpen(false);
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface TimeProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Lower bound as a `datetime-local` string (`YYYY-MM-DDTHH:mm`). */
  min?: string;
}

/**
 * Time-only sibling of {@link DiaryDateTimeField}. Renders the identical styled
 * hour/minute/AM-PM wheel popup, minus the calendar — the date half of the
 * `datetime-local` value is preserved untouched. Used where the date is pinned
 * to another field (e.g. an availability window's end, locked to the start day)
 * and only the time may change, so the End control matches the Start's look.
 */
export function DiaryTimeField({ id, value, onChange, disabled, min }: TimeProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const selected = useMemo(() => parseLocal(value), [value]);
  const minDate = useMemo(() => parseLocal(min), [min]);

  // Measure the trigger + popup and place the popup within the viewport,
  // flipping above when there isn't enough room below.
  const reposition = () => {
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popupH = popup?.offsetHeight ?? 300;
    const popupW = popup?.offsetWidth ?? 180;
    const vpH = window.innerHeight;
    const vpW = window.innerWidth;

    const spaceBelow = vpH - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < popupH + GAP && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - popupH - GAP : rect.bottom + GAP;
    top = Math.max(8, Math.min(top, vpH - popupH - 8));
    const left = Math.max(8, Math.min(rect.left, vpW - popupW - 8));
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    const onScroll = () => reposition();
    const onResize = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const displayValue = useMemo(() => {
    if (!selected) return "";
    const { h12, meridiem } = to12h(selected.getHours());
    return `${pad2(h12)}:${pad2(selected.getMinutes())} ${meridiem}`;
  }, [selected]);

  // Working date used to fill in the "other half" when nothing is picked yet.
  const base = selected ?? minDate ?? new Date();
  const { h12: curH12, meridiem: curMeridiem } = to12h(base.getHours());
  const curMinute = base.getMinutes();

  /** Clamp below-min values up to the minimum, then emit in the string contract. */
  const emit = (d: Date) => {
    const next = minDate && d.getTime() < minDate.getTime() ? new Date(minDate) : d;
    onChange(toLocal(next));
  };
  const pickHour = (h12: number) => {
    const d = new Date(base);
    d.setHours(to24h(h12, curMeridiem));
    emit(d);
  };
  const pickMinute = (mi: number) => {
    const d = new Date(base);
    d.setMinutes(mi);
    emit(d);
  };
  const pickMeridiem = (meridiem: "AM" | "PM") => {
    const d = new Date(base);
    d.setHours(to24h(curH12, meridiem));
    emit(d);
  };

  return (
    <div className="w-full min-w-0">
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "h-10 w-full px-3 flex items-center justify-between gap-2 rounded-md border text-sm outline-none transition-colors",
          "border-bridge-border/70 bg-bridge-surface/70 text-bridge-fg",
          open ? "border-bridge-accent/60 shadow-ring" : "hover:border-bridge-accent/40",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <span className={cn("truncate", !displayValue && "text-bridge-muted")}>
          {displayValue || "Select time"}
        </span>
        <Clock className="w-4 h-4 shrink-0 text-bridge-muted" />
      </button>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            data-floating-surface
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              visibility: coords ? "visible" : "hidden",
            }}
            className="z-[9999] flex rounded-md border border-bridge-border bg-bridge-surface shadow-xl overflow-hidden"
          >
            <TimeColumn items={HOURS_12} render={(h) => pad2(h)} selected={curH12} onPick={pickHour} loop />
            <TimeColumn items={MINUTES} render={(m) => pad2(m)} selected={curMinute} onPick={pickMinute} loop />
            <TimeColumn items={["AM", "PM"] as const} render={(m) => m} selected={curMeridiem} onPick={pickMeridiem} />
          </div>,
          document.body,
        )}
    </div>
  );
}

const ITEM_H = 36; // px, matches the `h-9` buttons below
const COL_H = 300; // px, matches the `h-[300px]` container
/** Odd number of stacked copies so the middle band can loop in both directions. */
const LOOP_COPIES = 5;

/**
 * A single time column. When {@link loop} is set, the items are stacked in
 * several identical copies and the scroll position is silently re-centred on
 * the middle band — giving an endless wheel (…12 → 01 → 02…) with no visible
 * scrollbar, matching the native Chrome picker.
 */
function TimeColumn<T extends string | number>({
  items,
  render,
  selected,
  onPick,
  loop,
}: {
  items: readonly T[];
  render: (item: T) => string;
  selected: T;
  onPick: (item: T) => void;
  loop?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const len = items.length;
  const rendered = loop ? Array.from({ length: LOOP_COPIES }, () => items).flat() : [...items];

  // Centre the selected item within the middle copy on mount.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = items.findIndex((x) => x === selected);
    const middleStart = loop ? Math.floor(LOOP_COPIES / 2) * len : 0;
    el.scrollTop = (middleStart + Math.max(idx, 0)) * ITEM_H - (COL_H / 2 - ITEM_H / 2);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the scroll position inside the middle band so the list never bottoms out.
  const handleScroll = () => {
    if (!loop) return;
    const el = ref.current;
    if (!el) return;
    const one = len * ITEM_H;
    const lower = one; // start of copy #1
    const upper = (LOOP_COPIES - 1) * one; // start of the last copy
    let st = el.scrollTop;
    while (st < lower) st += one;
    while (st >= upper) st -= one;
    if (st !== el.scrollTop) el.scrollTop = st;
  };

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className={cn(
        "h-[300px] w-14 overflow-y-auto px-1",
        // Hide the scrollbar across engines while keeping wheel/trackpad scroll.
        "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
      )}
    >
      {rendered.map((item, i) => {
        const isSelected = item === selected;
        return (
          <button
            key={`${String(item)}-${i}`}
            type="button"
            onClick={() => onPick(item)}
            className={cn(
              "w-full h-9 text-sm font-medium transition-colors",
              isSelected ? "bg-bridge-accent text-white" : "text-bridge-fg hover:bg-bridge-bg",
            )}
          >
            {render(item)}
          </button>
        );
      })}
    </div>
  );
}
